import { Card, Paginator, Table, Typography } from "@creditsafe/ui-components";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TableContainer,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { filterUsersBySearchText } from "../../../api/aml/utils";
import StatusCircle from "../../../shared/components/status-circle/StatusCircle";
import TableMenu from "../../../shared/components/table-menu/TableMenu";
import EllipsisTooltip from "../../../shared/components/tooltip/EllipsisTooltip";
import {
  assignedTo,
  kycReview,
  kycStatusLabel,
  riskRating,
  TableMenuItems,
} from "../../../shared/constants/common.constant";
import {
  DefaultNoDataFound,
  PaginatorWrapper,
  StyledIconButton,
} from "../../../shared/styles/common-styles";
import type { User } from "../../../shared/types/user.type";
import type { LocaleLanguages } from "../../../utils";
import { formatDate, showHyphenIfNoValue } from "../../../utils";
import {
  kycStatus,
  profileSortTypes,
  riskRatingEnums,
} from "../profile.constant";
import type { Profile } from "../types/profile.type";
import type { ChipLabelsType } from "./ProfileListContainer";
import { AssignedTo } from "./ProfileTableHeaderAssignedTo";
import { KYCReview } from "./ProfileTableHeaderKYCReview";
import { KYCStatus } from "./ProfileTableHeaderKYCStatus";
import { RiskRating } from "./ProfileTableHeaderRiskRating";
import { isIndividualOrBusiness } from "../utils";
import { usePermissionMap } from "../../../hooks";
import type MenuItemType from "../../../shared/types/MenuItemType.type";

const LinkWrapper = styled(Link)`
  &.link-wrapper {
    color: #000;
    cursor: pointer;
    text-decoration: underline;
  }
`;

const StyledCard = styled(Card)`
  &.table-component-card {
    margin: 0;
  }
`;

const StyledChip = styled(Chip)`
  margin-right: 5px !important;
`;

const StyledTypography = styled(Typography)`
  align-self: center;
  margin-right: 10px;
`;

const headings = [
  "profiles.profileName",
  "profiles.internalId",
  "profiles.riskRating",
  "profiles.kycStatus",
  "profiles.assignedTo",
  "profiles.modifiedAt",
  "profiles.kycApproved",
  "profiles.kycReview",
];

const ProfileListTable: React.FC<{
  activeUsers: User[] | undefined;
  profiles: Profile[];
  isIndeterminate: boolean;
  isAllChecked: boolean;
  totalCount: number;
  currentPageNumber: number;
  isFromSearch: boolean;
  isApiError: boolean;
  isFetching?: boolean;
  onProfileCheckChange: (_isSelected: boolean, _profile: Profile) => void;
  onHeaderCheckChange: () => void;
  pageChangeHandler: (_pageNumber: number) => void;
  onMenuAction: (_action: string, _actionProfile: Profile) => void;
  selectedStatus: (_status: string[]) => void;
  updateChipLabels: (_key: string, _value: string) => void;
  chipLabels: ChipLabelsType[];
  selectedAssignee: (_userId: (number | string)[]) => void;
  selectedBothDates: (_beforeDate: string, _afterDate: string) => void;
  statuses: string[];
  userNumbers: (number | string)[];
  reviewBeforeDate: string;
  reviewAfterDate: string;
  setRiskRating: (_riskRating: string[]) => void;
  selectedRiskRating: string[];
  sortType: string;
  onChangeProfileSortType: (_sortValue: string) => void;
  searchTerm: string;
}> = ({
  profiles,
  isIndeterminate,
  isAllChecked,
  totalCount,
  currentPageNumber,
  isFromSearch,
  isApiError,
  isFetching = false,
  onProfileCheckChange,
  onHeaderCheckChange,
  pageChangeHandler,
  onMenuAction,
  activeUsers,
  selectedStatus,
  updateChipLabels,
  chipLabels,
  selectedAssignee,
  selectedBothDates,
  statuses,
  userNumbers,
  reviewBeforeDate,
  reviewAfterDate,
  setRiskRating,
  selectedRiskRating,
  sortType,
  onChangeProfileSortType,
  searchTerm,
}) => {
  const { t: translate, i18n } = useTranslation(["profile", "common"]);
  const localeLanguage = i18n.language as LocaleLanguages;
  const navigate = useNavigate();
  const maxRecordAllowed = 10;
  const [searchText, setSearchText] = useState("");
  const [statusToDelete, setStatusToDelete] = useState<string[]>([]);
  const [userIdToDelete, setUserIdToDelete] = useState<number[]>([]);

  const permissions = usePermissionMap();

  const menuItems = useMemo<MenuItemType[]>(
    () => [
      {
        key: TableMenuItems.edit,
        label: TableMenuItems.edit,
        hidden: !permissions.ModifyProfile,
      },
      {
        key: TableMenuItems.assign,
        label: TableMenuItems.assign,
      },
      {
        key: TableMenuItems.delete,
        label: TableMenuItems.delete,
        hidden: !permissions.DeleteProfile,
      },
    ],
    [permissions]
  );

  const usersMenuList = useMemo(
    () => filterUsersBySearchText(activeUsers, searchText),
    [searchText, activeUsers]
  );

  const previousLabel = translate("prev", { ns: "common" });
  const nextLabel = translate("next", { ns: "common" });

  const headerChecked = (): void => {
    onHeaderCheckChange();
  };

  const profileChecked = (isSelected: boolean, profile: Profile): void => {
    onProfileCheckChange(isSelected, profile);
  };

  const handleMenuAction = (action: string, actionProfile: Profile): void => {
    onMenuAction(action, actionProfile);
    if (action === TableMenuItems.edit) {
      navigate(`/protect/profile/${actionProfile.id}`, {
        state: {
          isEditAction: true,
          page: currentPageNumber,
          riskRating: selectedRiskRating,
          statuses,
          userNumbers,
          reviewBeforeDate,
          reviewAfterDate,
          searchTerm,
          sortType,
        },
      });
    }
  };

  const setSearchTextValue = (text: string): void => {
    setSearchText(text);
  };

  const onPageChange = (pageNumber: number): void => {
    pageChangeHandler(pageNumber);
  };

  const handleDelete = (label: string): void => {
    updateChipLabels(label, "");
    switch (label) {
      case kycStatusLabel:
        setStatusToDelete([]);
        selectedStatus(statusToDelete);
        break;
      case kycReview:
        selectedBothDates("", "");
        break;
      case assignedTo:
        setUserIdToDelete([]);
        selectedAssignee(userIdToDelete);
        setSearchText("");
        break;
      case riskRating:
        setRiskRating([]);
        break;
      default:
        break;
    }
  };

  const getErrorMessage = (): string | null | undefined => {
    if (isApiError) {
      return translate("profiles.apiErrorMessage");
    } else if (
      isFromSearch ||
      chipLabels?.filter((chip) => chip?.value !== "").length > 0
    ) {
      return translate("profiles.noProfilesFoundForSearch");
    } else if (
      !isApiError &&
      !isFromSearch &&
      !chipLabels?.filter((chip) => chip?.value !== "").length
    ) {
      return translate("profile:profiles.noProfilesFound");
    }
    return undefined;
  };

  return (
    <>
      <Grid container spacing={2} style={{ paddingBottom: "10px" }}>
        <Grid item xs={10}>
          {chipLabels?.map(({ key, value }) => {
            if (value) {
              return (
                <StyledChip
                  aria-label={key}
                  key={key}
                  label={value}
                  onDelete={() => handleDelete(key)}
                  data-private="true"
                  deleteIcon={
                    <StyledIconButton aria-label={`${key} delete`}>
                      <CancelIcon />
                    </StyledIconButton>
                  }
                />
              );
            }
            return null;
          })}
        </Grid>
        <Grid container justifyContent={"flex-end"} item xs={2}>
          <StyledTypography
            variant={"body1"}
            align="center"
            aria-label="sortBy"
          >
            {translate("profile:profileSort.sortBy")}
          </StyledTypography>
          <Select
            size="small"
            inputProps={{
              "aria-label": "select-sort-type",
            }}
            value={sortType}
            onChange={(e) => onChangeProfileSortType(e.target.value)}
          >
            {profileSortTypes.map((item) => {
              return (
                <MenuItem key={item} value={item} aria-label={item}>
                  {translate(`profile:profileSort.${item}`)}
                </MenuItem>
              );
            })}
          </Select>
        </Grid>
      </Grid>
      <StyledCard title="" className={"table-component-card"}>
        <TableContainer>
          <Table data-test-id="test-id" aria-label="profile-list-table">
            <Table.Head aria-label="profile-list-table-head">
              <Table.Row>
                <Table.Cell component="td">
                  <Checkbox
                    checked={isAllChecked}
                    indeterminate={isIndeterminate}
                    onChange={headerChecked}
                    inputProps={{
                      "aria-label":
                        translate("profiles.selectAllProfiles") ?? "",
                    }}
                  />
                </Table.Cell>
                {headings.map((header, index) =>
                  header === "profiles.kycReview" ||
                  header === "profiles.kycStatus" ||
                  header === "profiles.assignedTo" ||
                  header === "profiles.riskRating" ? (
                    <Table.Cell component="th" key={`${header}}`}>
                      {header === "profiles.kycReview" && (
                        <KYCReview
                          header={header}
                          index={`profile-list-kyc-review-${index}`}
                          updateChipLabels={updateChipLabels}
                          selectedBothDates={selectedBothDates}
                          reviewBeforeDate={reviewBeforeDate}
                          reviewAfterDate={reviewAfterDate}
                        />
                      )}
                      {header === "profiles.kycStatus" && (
                        <KYCStatus
                          header={header}
                          index={`profile-list-kyc-status-${index}`}
                          kycStatus={kycStatus}
                          updateChipLabels={updateChipLabels}
                          selectedStatus={selectedStatus}
                          statusToDelete={statusToDelete}
                          statuses={statuses}
                        />
                      )}
                      {header === "profiles.assignedTo" && (
                        <AssignedTo
                          header={header}
                          index={`profile-list-kyc-assigned-to-${index}`}
                          userSelected={userNumbers}
                          updateChipLabels={updateChipLabels}
                          usersMenuList={usersMenuList}
                          setSearchTextValue={setSearchTextValue}
                          selectedAssignee={selectedAssignee}
                          searchText={searchText}
                        />
                      )}
                      {header === "profiles.riskRating" && (
                        <RiskRating
                          header={header}
                          index={`profile-list-kyc-risk-rating-${index}`}
                          riskRating={riskRatingEnums}
                          updateChipLabels={updateChipLabels}
                          setRiskRating={setRiskRating}
                          selectedRiskRating={selectedRiskRating}
                        />
                      )}
                    </Table.Cell>
                  ) : (
                    <Typography
                      key={header}
                      aria-label="profileListTableValue"
                      component="th"
                      variant="h6"
                    >
                      {translate(header)}
                    </Typography>
                  )
                )}
              </Table.Row>
            </Table.Head>
            {isFetching ? (
              <Table.Body>
                <Table.Row>
                  <Table.Cell
                    colSpan={10}
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    <CircularProgress size={40} aria-label="Loading Profiles" />
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            ) : (
              <Table.Body aria-label="profile-list-table-body">
                {profiles.map((profile: Profile) => (
                  <Table.Row
                    key={`profile-table-row-${profile.id}`}
                    data-private="true"
                    aria-label={`profile-row-${profile.id}`}
                  >
                    <Table.Cell aria-label={`profile-checkbox-${profile.id}`}>
                      <Checkbox
                        checked={!!profile.isSelected}
                        onChange={(e) =>
                          profileChecked(e.target.checked, profile)
                        }
                        inputProps={{
                          "aria-label":
                            translate("profiles.selectProfile", {
                              name: profile.name,
                            }) ?? "",
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        maxWidth: "170px",
                      }}
                      aria-label={`profile-name-${profile.id}`}
                    >
                      <EllipsisTooltip content={profile.name}>
                        <LinkWrapper
                          role="a"
                          to={`/protect/profile/${
                            profile.id
                          }/${isIndividualOrBusiness(profile.type)}/details`}
                          state={{
                            type: profile.type,
                            page: currentPageNumber,
                            riskRating: selectedRiskRating,
                            statuses,
                            userNumbers,
                            reviewBeforeDate,
                            reviewAfterDate,
                            searchTerm,
                            sortType,
                          }}
                          className="link-wrapper"
                          aria-label="View-profile-details-link"
                          data-private="true"
                        >
                          {profile.name}
                        </LinkWrapper>
                      </EllipsisTooltip>
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        maxWidth: "170px",
                      }}
                      aria-label={`profile-internal-id-${profile.id}`}
                    >
                      <EllipsisTooltip
                        content={profile.internalId}
                        data-private="true"
                      >
                        {String(showHyphenIfNoValue(profile.internalId))}
                      </EllipsisTooltip>
                    </Table.Cell>
                    <Table.Cell
                      aria-label={`profile-risk-rating-${profile.id}`}
                    >
                      <StatusCircle
                        status={
                          profile.riskRating === "notApplicable"
                            ? profile.riskRating
                            : profile.riskRating + "Risk"
                        }
                        text={translate("riskRatings." + profile.riskRating, {
                          ns: "common",
                        })}
                        className={profile.riskRating}
                      ></StatusCircle>
                    </Table.Cell>
                    <Table.Cell aria-label={`profile-status-${profile.id}`}>
                      <StatusCircle
                        status={profile.status}
                        text={translate("profileStatus." + profile.status, {
                          ns: "profile",
                        })}
                        className={profile.riskRating}
                      ></StatusCircle>
                    </Table.Cell>
                    <Table.Cell
                      aria-label={`profile-assigned-to-${profile.id}`}
                    >
                      {showHyphenIfNoValue(profile.assignedTo)}
                    </Table.Cell>
                    <Table.Cell
                      aria-label={`profile-modified-at-${profile.id}`}
                    >
                      {formatDate(profile.modifiedAt, localeLanguage)}
                    </Table.Cell>
                    <Table.Cell
                      aria-label={`profile-kyc-approved-at-${profile.id}`}
                    >
                      {formatDate(profile.kycApprovedAt, localeLanguage)}
                    </Table.Cell>
                    <Table.Cell
                      aria-label={`profile-kyc-review-on-${profile.id}`}
                    >
                      {formatDate(profile.kycReviewOn, localeLanguage)}
                    </Table.Cell>
                    <Table.Cell aria-label={`profile-menu-${profile.id}`}>
                      <TableMenu
                        menuItems={menuItems}
                        onMenuClick={(action) =>
                          handleMenuAction(action, profile)
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            )}
          </Table>
        </TableContainer>
        {!totalCount && (
          <DefaultNoDataFound>
            <Typography
              variant={"body2"}
              align="center"
              aria-label="No data found error message"
            >
              {getErrorMessage()}
            </Typography>
          </DefaultNoDataFound>
        )}
        {totalCount > maxRecordAllowed && (
          <PaginatorWrapper>
            <Paginator
              onPageChange={onPageChange}
              pageNumber={currentPageNumber}
              previousLabel={previousLabel}
              nextLabel={nextLabel}
              nextPageAvailable={false}
              datatestId="profileData"
              offset={1}
              totalPages={Math.ceil(totalCount / maxRecordAllowed)}
            />
          </PaginatorWrapper>
        )}
      </StyledCard>
    </>
  );
};

export default ProfileListTable;
